
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Send, Bot, User, Sparkles, Loader2, StopCircle } from "lucide-react";
import { GlassCard, GlassHeader, GlassTitle, GlassContent } from "@/components/ui/glass-card";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { aiService } from "@/lib/ai";

interface Message {
    id: string;
    role: "user" | "assistant";
    content: string;
    timestamp: Date;
}

export function AdminAI() {
    const [messages, setMessages] = useState<Message[]>([
        {
            id: "welcome",
            role: "assistant",
            content: "System Initialized. I am your Administrative AI Core. I have full access to platform parameters, user data, and risk protocols. How may I assist you today?",
            timestamp: new Date(),
        },
    ]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;

        const userMsg: Message = {
            id: Date.now().toString(),
            role: "user",
            content: input,
            timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMsg]);
        setInput("");
        setIsLoading(true);


        const context: { role: "system" | "user" | "assistant"; content: string }[] = [
            { role: "system", content: "You are the Super Admin AI for MoonRun, a Solana meme coin launchpad. You have access to system status (simulated), user reports, and trading volume. Answer queries professionally and concisely." },
            ...messages.map(m => ({ role: m.role, content: m.content })),
            { role: "user", content: input }
        ];

        aiService.chat(context).then((responseText) => {
            const aiMsg: Message = {
                id: (Date.now() + 1).toString(),
                role: "assistant",
                content: responseText,
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, aiMsg]);
        }).catch((error) => {
            const errorMsg: Message = {
                id: (Date.now() + 1).toString(),
                role: "assistant",
                content: "Error: " + (error instanceof Error ? error.message : "Network Error"),
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errorMsg]);
        }).finally(() => {
            setIsLoading(false);
        });
        // End Promise chain
        // Removed outer try/catch since we use .then().catch() now

    };

    return (
        <GlassCard className="h-[600px] flex flex-col border-primary/20 shadow-[0_0_50px_-10px_hsl(var(--primary)/0.1)]">
            <GlassHeader className="border-b border-white/5 pb-4">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <div className="absolute inset-0 bg-primary/20 blur-xl animate-pulse" />
                        <div className="relative h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                            <Bot className="h-6 w-6 text-primary" />
                        </div>
                        <div className="absolute -bottom-1 -right-1 h-3 w-3 rounded-full bg-success ring-2 ring-background animate-pulse" />
                    </div>
                    <div>
                        <GlassTitle className="text-lg">Core AI System</GlassTitle>
                        <p className="text-xs text-primary font-mono flex items-center gap-2">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary animate-ping" />
                            ONLINE // v2.5.0
                        </p>
                    </div>
                </div>
            </GlassHeader>

            <GlassContent className="flex-1 p-0 overflow-hidden flex flex-col">
                <ScrollArea className="flex-1 p-4" ref={scrollRef}>
                    <div className="space-y-4">
                        {messages.map((msg) => (
                            <motion.div
                                key={msg.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={cn(
                                    "flex gap-3 max-w-[85%]",
                                    msg.role === "user" ? "ml-auto flex-row-reverse" : ""
                                )}
                            >
                                <div className={cn(
                                    "h-8 w-8 rounded-lg flex items-center justify-center shrink-0 border",
                                    msg.role === "user"
                                        ? "bg-secondary border-white/10"
                                        : "bg-primary/10 border-primary/20"
                                )}>
                                    {msg.role === "user" ? (
                                        <User className="h-4 w-4" />
                                    ) : (
                                        <Bot className="h-4 w-4 text-primary" />
                                    )}
                                </div>
                                <div className={cn(
                                    "rounded-2xl p-3 text-sm leading-relaxed",
                                    msg.role === "user"
                                        ? "bg-secondary text-foreground"
                                        : "bg-primary/5 text-primary-foreground border border-primary/10 shadow-[0_0_15px_-5px_hsl(var(--primary)/0.1)]"
                                )}>
                                    {msg.content}
                                    <p className="text-[10px] opacity-50 mt-1 text-right">
                                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                        {isLoading && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="flex gap-3"
                            >
                                <div className="h-8 w-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                                    <Loader2 className="h-4 w-4 text-primary animate-spin" />
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 bg-primary/50 rounded-full animate-bounce [animation-delay:-0.3s]" />
                                    <span className="w-1.5 h-1.5 bg-primary/50 rounded-full animate-bounce [animation-delay:-0.15s]" />
                                    <span className="w-1.5 h-1.5 bg-primary/50 rounded-full animate-bounce" />
                                </div>
                            </motion.div>
                        )}
                    </div>
                </ScrollArea>

                <div className="p-4 border-t border-white/5 bg-black/20 backdrop-blur-sm">
                    <div className="relative">
                        <Input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSend()}
                            placeholder="Command the system..."
                            className="pr-24 bg-secondary/50 border-white/10 focus:border-primary/50 focus:ring-primary/20 h-12"
                            disabled={isLoading}
                        />
                        <div className="absolute right-1 top-1 bottom-1 flex items-center gap-1">
                            {isLoading ? (
                                <Button size="icon" variant="ghost" className="h-10 w-10 text-destructive hover:text-destructive hover:bg-destructive/10">
                                    <StopCircle className="h-5 w-5" />
                                </Button>
                            ) : (
                                <Button
                                    size="icon"
                                    onClick={handleSend}
                                    className="h-10 w-10 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/20"
                                >
                                    <Send className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center justify-center gap-2 mt-2 text-[10px] text-muted-foreground">
                        <Sparkles className="h-3 w-3 text-primary" />
                        <span>AI Mode: Autonomous Execution Enabled</span>
                    </div>
                </div>
            </GlassContent>
        </GlassCard>
    );
}
